'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import {
  Phone,
  MapPin,
  X,
  User,
  LogOut,
  Menu,
  ChevronDown,
  Calendar,
  FileText,
  FlaskConical,
  Pill,
  Heart,
  CreditCard,
  QrCode,
  Stethoscope,
  Activity,
  Navigation,
} from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { hospitalConfig } from '@/data/hospitalConfig';
import LocationFinder from '@/components/locations/LocationFinder';
import { useSelectedCentre } from '@/context/SelectedCentreContext';
import { useAuth } from '@/contexts/AuthContext';
import type { Centre } from '@/data/locations';
import Icon from '@/components/ui/AppIcon';


interface TopNavProps {
  showBackButton?: boolean;
  backHref?: string;
  backLabel?: string;
  transparent?: boolean;
}

const PRIMARY_NAV = [
  { label: 'FIND A DOCTOR', href: '/find-doctor', icon: Stethoscope },
  { label: 'DIAGNOSTICS', href: '/diagnostics', icon: FlaskConical },
  { label: 'HEALTH CHECK-UPS', href: '/health-checkup', icon: Activity },
  { label: 'PHARMACY', href: '/pharmacy', icon: Pill },
  { label: 'CARE NAVIGATOR', href: '/care-journey-screen', icon: Heart },
];

const MY_AIW_LINKS = [
  { label: 'MY APPOINTMENTS', href: '/my-all-is-well', icon: Calendar, section: 'appointments' },
  { label: 'MY PRESCRIPTIONS', href: '/my-all-is-well', icon: FileText, section: 'prescriptions' },
  { label: 'MY LAB REPORTS', href: '/my-all-is-well', icon: FlaskConical, section: 'lab-reports' },
  { label: 'MY MEDICINES', href: '/my-all-is-well', icon: Pill, section: 'medicines' },
  { label: 'MY VISITS', href: '/my-all-is-well', icon: Navigation, section: 'visits' },
  { label: 'DIGITAL HEALTH PASSPORT', href: '/health-passport', icon: QrCode, section: 'passport' },
  { label: 'MY MEMBERSHIP', href: '/memberships', icon: CreditCard, section: 'membership' },
];

export default function TopNav({
  showBackButton = false,
  backHref = '/',
  backLabel = 'Back',
  transparent = false,
}: TopNavProps) {
  const [locationOpen, setLocationOpen] = useState(false);
  const { selectedCentre, setSelectedCentre, clearCentre } = useSelectedCentre();
  const { patient, isAuthenticated, logout } = useAuth();

  const [myAiwOpen, setMyAiwOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMyAiwOpen, setMobileMyAiwOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const myAiwRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Scroll detection for sticky shrink
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close MY AIW dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (myAiwRef.current && !myAiwRef.current.contains(e.target as Node)) {
        setMyAiwOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleSelectCentre = useCallback((centre: Centre) => {
    setSelectedCentre(centre);
  }, [setSelectedCentre]);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    setMobileMyAiwOpen(false);
  }, []);

  const headerHeight = scrolled ? 'h-[68px]' : 'h-[80px]';
  const navBg = transparent && !scrolled
    ? 'bg-transparent'
    : scrolled
      ? 'bg-[#0a1628]/98 shadow-[0_2px_24px_rgba(0,0,0,0.4)] backdrop-blur-md'
      : 'bg-[#0a1628]/95 backdrop-blur-sm';

  return (
    <>
      {/* ─── MAIN HEADER ─────────────────────────────────────────── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${navBg}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className={`flex items-center justify-between px-5 lg:px-8 xl:px-12 transition-all duration-300 ${headerHeight}`}>

          {/* ── LEFT: Logo + Wordmark ─────────────────────────────── */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link
              href="/"
              className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6]/60 rounded-lg"
              aria-label="All Is Well Experience — Home"
            >
              <div className="flex-shrink-0 bg-white/8 rounded-xl px-2.5 py-1.5 group-hover:bg-white/12 transition-colors duration-200">
                <AppImage
                  src={hospitalConfig.logo}
                  alt="All Is Well Hospital Logo"
                  width={72}
                  height={28}
                  className={`w-auto object-contain transition-all duration-300 ${scrolled ? 'h-6' : 'h-7'}`}
                  priority
                />
              </div>
              <div className="hidden sm:flex flex-col leading-none">
                <span className="text-[10px] font-bold tracking-[0.22em] text-white/90 uppercase">ALL IS WELL</span>
                <span className="text-[9px] font-semibold tracking-[0.18em] text-[#8b5cf6]/80 uppercase mt-0.5">EXPERIENCE™</span>
              </div>
            </Link>
          </div>

          {/* ── CENTER: Primary Nav (desktop) ────────────────────── */}
          <div className="hidden lg:flex items-center gap-0.5 xl:gap-1">
            {PRIMARY_NAV.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="relative group px-3 xl:px-4 py-2 text-[10.5px] xl:text-[11px] font-semibold tracking-[0.14em] text-white/55 hover:text-white/90 transition-colors duration-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6]/60 rounded-md"
              >
                {item.label}
                {/* Hover underline */}
                <span
                  className="absolute bottom-0 left-3 xl:left-4 right-3 xl:right-4 h-px bg-gradient-to-r from-[#8b5cf6]/0 via-[#8b5cf6]/70 to-[#8b5cf6]/0 scale-x-0 group-hover:scale-x-100 transition-transform duration-250 origin-center"
                  aria-hidden="true"
                />
              </Link>
            ))}
          </div>

          {/* ── RIGHT: Actions ───────────────────────────────────── */}
          <div className="flex items-center gap-1.5 xl:gap-2 flex-shrink-0">

            {/* MY ALL IS WELL */}
            <div className="hidden md:block relative" ref={myAiwRef}>
              <button
                onClick={() => setMyAiwOpen(v => !v)}
                className={`flex items-center gap-1.5 text-[10.5px] font-semibold tracking-[0.12em] px-3 xl:px-4 py-2 rounded-lg border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6]/60 whitespace-nowrap ${
                  isAuthenticated
                    ? 'border-[#8b5cf6]/50 text-[#8b5cf6]/90 hover:text-[#8b5cf6] hover:border-[#8b5cf6]/80 hover:bg-[#8b5cf6]/8'
                    : 'border-[#8b5cf6]/30 text-white/60 hover:text-white/85 hover:border-[#8b5cf6]/50 hover:bg-[#8b5cf6]/6'
                }`}
                aria-label="My All Is Well — Personal care area"
                aria-expanded={myAiwOpen}
                aria-haspopup="true"
              >
                <User size={11} aria-hidden="true" />
                <span>
                  {isAuthenticated && patient
                    ? patient.firstName.toUpperCase()
                    : 'MY ALL IS WELL'}
                </span>
                <ChevronDown
                  size={10}
                  aria-hidden="true"
                  className={`transition-transform duration-200 ${myAiwOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* MY ALL IS WELL Dropdown Panel */}
              {myAiwOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-72 rounded-2xl overflow-hidden shadow-[0_8px_48px_rgba(0,0,0,0.5)] border border-white/8 z-50"
                  style={{
                    background: 'linear-gradient(160deg, #0f1f3d 0%, #0a1628 60%, #0d1a35 100%)',
                    animation: 'myAiwFadeIn 0.22s cubic-bezier(0.16,1,0.3,1) both',
                  }}
                  role="dialog"
                  aria-label="My All Is Well panel"
                >
                  {/* Panel header */}
                  <div className="px-5 pt-5 pb-4 border-b border-white/6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[11px] font-bold tracking-[0.2em] text-white/90 uppercase">MY ALL IS WELL</p>
                        <p className="text-[10px] text-white/40 mt-0.5 tracking-wide">
                          {isAuthenticated && patient
                            ? `Welcome, ${patient.firstName}`
                            : 'Your personal care experience.'}
                        </p>
                      </div>
                      <button
                        onClick={() => setMyAiwOpen(false)}
                        className="text-white/25 hover:text-white/60 transition-colors p-1 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                        aria-label="Close My All Is Well panel"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>

                  {isAuthenticated && patient ? (
                    /* ── LOGGED IN STATE ── */
                    <>
                      <div className="px-3 py-2">
                        {MY_AIW_LINKS.map((link) => {
                          const Icon = link.icon;
                          return (
                            <Link
                              key={link.section}
                              href={link.href}
                              onClick={() => setMyAiwOpen(false)}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/55 hover:text-white/90 hover:bg-white/5 transition-all duration-150 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6]/40"
                            >
                              <Icon size={13} className="text-[#8b5cf6]/60 group-hover:text-[#8b5cf6]/90 transition-colors flex-shrink-0" aria-hidden="true" />
                              <span className="text-[10.5px] font-semibold tracking-[0.12em]">{link.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                      <div className="px-3 pb-3 border-t border-white/6 pt-2">
                        <button
                          onClick={async () => { await logout(); setMyAiwOpen(false); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/35 hover:text-white/65 hover:bg-white/4 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20"
                        >
                          <LogOut size={13} aria-hidden="true" />
                          <span className="text-[10.5px] font-semibold tracking-[0.12em]">LOG OUT</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    /* ── LOGGED OUT STATE ── */
                    <div className="px-5 py-5">
                      <p className="text-[11px] font-bold tracking-[0.16em] text-white/80 mb-1">WELCOME TO MY ALL IS WELL</p>
                      <p className="text-[10px] text-white/40 leading-relaxed mb-5">
                        Access your appointments, prescriptions, reports and care journey in one place.
                      </p>
                      <Link
                        href="/my-all-is-well/login"
                        onClick={() => setMyAiwOpen(false)}
                        className="block w-full text-center py-2.5 px-4 rounded-xl bg-[#8b5cf6] hover:bg-[#7c3aed] text-white text-[10.5px] font-bold tracking-[0.16em] transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6]/60"
                      >
                        LOGIN / SIGN UP
                      </Link>
                      <div className="mt-4 pt-4 border-t border-white/6 space-y-0.5">
                        {MY_AIW_LINKS.map((link) => {
                          const Icon = link.icon;
                          return (
                            <Link
                              key={link.section}
                              href="/my-all-is-well/login"
                              onClick={() => setMyAiwOpen(false)}
                              className="flex items-center gap-3 px-2 py-2 rounded-lg text-white/30 hover:text-white/55 hover:bg-white/4 transition-all duration-150 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6]/30"
                            >
                              <Icon size={12} className="text-white/20 group-hover:text-[#8b5cf6]/50 transition-colors flex-shrink-0" aria-hidden="true" />
                              <span className="text-[10px] font-semibold tracking-[0.1em]">{link.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* LOCATIONS */}
            <button
              onClick={() => setLocationOpen(true)}
              className="hidden sm:flex items-center gap-1.5 text-[10.5px] font-semibold tracking-[0.12em] px-3 py-2 rounded-lg text-white/55 hover:text-white/85 hover:bg-white/6 transition-all duration-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              aria-label="Find an All Is Well Centre"
            >
              <MapPin size={11} aria-hidden="true" />
              <span>LOCATIONS</span>
            </button>

            {/* EMERGENCY */}
            <a
              href={hospitalConfig.emergencyTel}
              className="flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.14em] px-3 py-2 rounded-lg border border-red-800/60 text-red-400 hover:text-red-300 hover:border-red-600/80 hover:bg-red-950/30 transition-all duration-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50"
              aria-label="Emergency — Call All Is Well Hospital immediately"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" aria-hidden="true" />
              <span>EMERGENCY</span>
            </a>

            {/* CALL */}
            <a
              href={hospitalConfig.phoneTel}
              className="hidden lg:flex items-center gap-1.5 text-[10.5px] font-semibold tracking-[0.1em] px-3 py-2 rounded-lg text-white/45 hover:text-white/75 hover:bg-white/5 transition-all duration-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              aria-label={`Call All Is Well: ${hospitalConfig.phone}`}
            >
              <Phone size={11} aria-hidden="true" />
              <span className="hidden xl:inline">{hospitalConfig.phone}</span>
            </a>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg text-white/60 hover:text-white hover:bg-white/8 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ml-1"
              aria-label="Open navigation menu"
              aria-expanded={mobileMenuOpen}
            >
              <Menu size={18} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Selected centre indicator */}
        {selectedCentre && (
          <div className="flex items-center justify-center gap-2 px-4 py-1.5 bg-[#8b5cf6]/10 border-t border-[#8b5cf6]/15">
            <MapPin size={9} className="text-[#8b5cf6]/70 flex-shrink-0" aria-hidden="true" />
            <span className="text-[10px] text-[#8b5cf6]/80 font-semibold tracking-wider truncate max-w-xs">
              {selectedCentre.shortName}
            </span>
            <button
              onClick={() => setLocationOpen(true)}
              className="text-[9px] text-[#8b5cf6]/45 hover:text-[#8b5cf6]/80 transition-colors tracking-widest uppercase focus:outline-none focus-visible:ring-1 focus-visible:ring-[#8b5cf6]/40 rounded"
              aria-label="Change selected centre"
            >
              CHANGE
            </button>
            <button
              onClick={clearCentre}
              className="text-white/20 hover:text-white/50 transition-colors ml-0.5 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 rounded"
              aria-label="Clear selected centre"
            >
              <X size={9} />
            </button>
          </div>
        )}
      </nav>

      {/* ─── MOBILE FULL-SCREEN DRAWER ───────────────────────────── */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeMobileMenu}
            aria-hidden="true"
            style={{ animation: 'fadeIn 0.2s ease both' }}
          />

          {/* Drawer panel */}
          <div
            ref={mobileMenuRef}
            className="absolute top-0 right-0 bottom-0 w-full max-w-sm flex flex-col"
            style={{
              background: 'linear-gradient(160deg, #0f1f3d 0%, #0a1628 100%)',
              animation: 'slideInRight 0.28s cubic-bezier(0.16,1,0.3,1) both',
            }}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
              <Link href="/" onClick={closeMobileMenu} className="flex items-center gap-2.5">
                <div className="bg-white/8 rounded-lg px-2 py-1">
                  <AppImage
                    src={hospitalConfig.logo}
                    alt="All Is Well Hospital Logo"
                    width={60}
                    height={24}
                    className="h-6 w-auto object-contain"
                  />
                </div>
                <div className="flex flex-col leading-none">
                  <span className="text-[9px] font-bold tracking-[0.2em] text-white/85 uppercase">ALL IS WELL</span>
                  <span className="text-[8px] font-semibold tracking-[0.16em] text-[#8b5cf6]/70 uppercase mt-0.5">EXPERIENCE™</span>
                </div>
              </Link>
              <button
                onClick={closeMobileMenu}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-white/50 hover:text-white hover:bg-white/8 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                aria-label="Close navigation menu"
              >
                <X size={18} />
              </button>
            </div>

            {/* Drawer content — scrollable */}
            <div className="flex-1 overflow-y-auto py-3 px-3">

              {/* Primary care actions */}
              <div className="mb-1">
                <p className="text-[9px] font-bold tracking-[0.22em] text-white/25 uppercase px-3 py-2">CARE</p>
                {PRIMARY_NAV.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={closeMobileMenu}
                      className="flex items-center gap-3.5 px-3 py-3.5 rounded-xl text-white/70 hover:text-white hover:bg-white/6 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6]/40"
                    >
                      <Icon size={15} className="text-[#8b5cf6]/50 flex-shrink-0" aria-hidden="true" />
                      <span className="text-[12px] font-semibold tracking-[0.12em]">{item.label}</span>
                    </Link>
                  );
                })}
              </div>

              <div className="h-px bg-white/6 mx-3 my-2" />

              {/* Utility */}
              <div className="mb-1">
                <p className="text-[9px] font-bold tracking-[0.22em] text-white/25 uppercase px-3 py-2">UTILITY</p>
                <button
                  onClick={() => { closeMobileMenu(); setTimeout(() => setLocationOpen(true), 100); }}
                  className="w-full flex items-center gap-3.5 px-3 py-3.5 rounded-xl text-white/70 hover:text-white hover:bg-white/6 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                >
                  <MapPin size={15} className="text-white/35 flex-shrink-0" aria-hidden="true" />
                  <span className="text-[12px] font-semibold tracking-[0.12em]">LOCATIONS</span>
                </button>
              </div>

              <div className="h-px bg-white/6 mx-3 my-2" />

              {/* MY ALL IS WELL */}
              <div className="mb-1">
                <button
                  onClick={() => setMobileMyAiwOpen(v => !v)}
                  className="w-full flex items-center justify-between gap-3.5 px-3 py-3.5 rounded-xl text-white/70 hover:text-white hover:bg-white/6 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6]/40"
                  aria-expanded={mobileMyAiwOpen}
                >
                  <div className="flex items-center gap-3.5">
                    <User size={15} className="text-[#8b5cf6]/60 flex-shrink-0" aria-hidden="true" />
                    <span className="text-[12px] font-semibold tracking-[0.12em]">
                      {isAuthenticated && patient ? `MY ALL IS WELL — ${patient.firstName.toUpperCase()}` : 'MY ALL IS WELL'}
                    </span>
                  </div>
                  <ChevronDown
                    size={13}
                    className={`text-white/30 transition-transform duration-200 ${mobileMyAiwOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>

                {mobileMyAiwOpen && (
                  <div className="mt-1 ml-3 pl-4 border-l border-[#8b5cf6]/20 space-y-0.5">
                    {isAuthenticated && patient ? (
                      <>
                        {MY_AIW_LINKS.map((link) => {
                          const Icon = link.icon;
                          return (
                            <Link
                              key={link.section}
                              href={link.href}
                              onClick={closeMobileMenu}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/55 hover:text-white/85 hover:bg-white/5 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6]/30"
                            >
                              <Icon size={12} className="text-[#8b5cf6]/50 flex-shrink-0" aria-hidden="true" />
                              <span className="text-[11px] font-semibold tracking-[0.1em]">{link.label}</span>
                            </Link>
                          );
                        })}
                        <button
                          onClick={async () => { await logout(); closeMobileMenu(); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/35 hover:text-white/60 hover:bg-white/4 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20"
                        >
                          <LogOut size={12} aria-hidden="true" />
                          <span className="text-[11px] font-semibold tracking-[0.1em]">LOG OUT</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-[10px] text-white/35 px-3 py-2 leading-relaxed">
                          Access your appointments, prescriptions, reports and care journey.
                        </p>
                        <Link
                          href="/my-all-is-well/login"
                          onClick={closeMobileMenu}
                          className="flex items-center justify-center gap-2 mx-3 py-2.5 rounded-xl bg-[#8b5cf6] hover:bg-[#7c3aed] text-white text-[11px] font-bold tracking-[0.14em] transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6]/60"
                        >
                          LOGIN / SIGN UP
                        </Link>
                        <div className="mt-2 space-y-0.5">
                          {MY_AIW_LINKS.map((link) => {
                            const Icon = link.icon;
                            return (
                              <Link
                                key={link.section}
                                href="/my-all-is-well/login"
                                onClick={closeMobileMenu}
                                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/30 hover:text-white/55 hover:bg-white/4 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6]/30"
                              >
                                <Icon size={12} className="text-white/20 flex-shrink-0" aria-hidden="true" />
                                <span className="text-[11px] font-semibold tracking-[0.1em]">{link.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="h-px bg-white/6 mx-3 my-2" />

              {/* Contact */}
              <div>
                <p className="text-[9px] font-bold tracking-[0.22em] text-white/25 uppercase px-3 py-2">CONTACT</p>
                <a
                  href={hospitalConfig.phoneTel}
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3.5 px-3 py-3.5 rounded-xl text-white/60 hover:text-white hover:bg-white/6 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                >
                  <Phone size={15} className="text-white/35 flex-shrink-0" aria-hidden="true" />
                  <span className="text-[12px] font-semibold tracking-[0.12em]">CALL {hospitalConfig.phone}</span>
                </a>
                <a
                  href={hospitalConfig.officialWhatsAppLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3.5 px-3 py-3.5 rounded-xl text-white/60 hover:text-white hover:bg-white/6 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500/40"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="text-green-500/60 flex-shrink-0" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="text-[12px] font-semibold tracking-[0.12em]">WHATSAPP</span>
                </a>
              </div>
            </div>

            {/* Drawer footer — EMERGENCY always visible */}
            <div className="px-3 pb-4 pt-2 border-t border-white/8">
              <a
                href={hospitalConfig.emergencyTel}
                className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-xl border border-red-700/60 bg-red-950/30 text-red-400 hover:text-red-300 hover:bg-red-950/50 hover:border-red-600/80 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50"
                aria-label="Emergency — Call All Is Well Hospital immediately"
              >
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" aria-hidden="true" />
                <span className="text-[12px] font-bold tracking-[0.2em]">EMERGENCY</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* LocationFinder modal */}
      <LocationFinder
        isOpen={locationOpen}
        onClose={() => setLocationOpen(false)}
        onSelectCentre={handleSelectCentre}
        selectedCentreId={selectedCentre?.id}
      />

      {/* Keyframe animations */}
      <style>{`
        @keyframes myAiwFadeIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </>
  );
}